import { Col, DatePicker, Form, Input, Row } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import { dateFormat } from '../../../utils'
import dayjs from 'dayjs'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DraggableModal } from '../../../components/DraggableModal'
import { Assignment, EntKurultaiRegister } from '../../../models/kurultai/kutultaiRegister'
import { AddKurultaiItems } from './AddKurultaiItems'
import { MapAddSingleMarker } from '../../../components/maps/MapAddPoint'
import { GeoJsonPoint, Geometry } from '../../../models/geoJsonPoint'
import SelectTerritorialUnitsCode from '../../../components/selects/SelectTerritorialUnitsCode'

enum FormField {
	assemblyNumber,
	assemblyDate,
	delegateName,
	territorialUnitRegion,
	territorialUnitArea,
	territorialUnit,
	latitude,
	longitude,
}

interface IProps {
	show: boolean
	kurultaiRegister: EntKurultaiRegister
	onHide: () => void
	onSubmit: (pos: EntKurultaiRegister) => Promise<boolean>
}

export const AddKurultaiRegisterModal = (props: IProps) => {
	const { t } = useTranslation()
	const [form] = useForm()
	const [loading, setLoading] = useState<boolean>(false)
	const [items, setItems] = useState<Assignment[]>([])
	const [geoJsonData, setGeoJsonData] = useState<GeoJsonPoint | null>(null)
	const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null)
	const [selectedRegionCode, setSelectedRegionCode] = useState<string | null>(null)
	const [selectedAreaCode, setSelectedAreaCode] = useState<string | null>(null)
	const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null)
	const [selectedRegionPolygonJson, setSelectedRegionPolygonJson] = useState<string | null>(null)
	const [geoError, setGeoError] = useState<boolean>(false)

	useEffect(() => {
		if (props.show) {
			setFieldsFromEntity()
		}
	}, [props.show])

	useEffect(() => {
		updateGeoJsonPoint()
	}, [props.kurultaiRegister.latitude, props.kurultaiRegister.longitude])

	const updateGeoJsonPoint = () => {
		const { latitude, longitude } = props.kurultaiRegister
		if (latitude != null && longitude != null) {
			const point = new GeoJsonPoint()
			point.geometry = new Geometry()
			point.geometry.coordinates = [longitude, latitude]
			setGeoJsonData(point)
		} else {
			setGeoJsonData(null)
		}
	}

	const setFieldsFromEntity = (): void => {
		form.setFields([
			{ name: FormField.assemblyNumber, value: props.kurultaiRegister.assemblyNumber },
			{
				name: FormField.assemblyDate,
				value: props.kurultaiRegister.assemblyDate
					? dayjs(props.kurultaiRegister.assemblyDate)
					: null,
			},
			{ name: FormField.delegateName, value: props.kurultaiRegister.delegateName },
			{ name: FormField.latitude, value: props.kurultaiRegister.latitude },
			{ name: FormField.longitude, value: props.kurultaiRegister.longitude },
			{ name: FormField.territorialUnitRegion, value: props.kurultaiRegister.regionId },
			{
				name: FormField.territorialUnitArea,
				value: props.kurultaiRegister.territorialUnit
					? props.kurultaiRegister.territorialUnit.code
					: null,
			},
			{ name: FormField.territorialUnit, value: props.kurultaiRegister.territorialUnitId },
		])

		if (props.kurultaiRegister.regionId) {
			setSelectedRegionId(props.kurultaiRegister.regionId)
		}
		if (props.kurultaiRegister.territorialUnit && props.kurultaiRegister.territorialUnit.code) {
			setSelectedAreaCode(props.kurultaiRegister.territorialUnit.code)
		}
		if (props.kurultaiRegister.territorialUnit && props.kurultaiRegister.territorialUnit.id) {
			setSelectedAreaId(props.kurultaiRegister.territorialUnit.id)
		}

		const defaultAssignments: Assignment[] = [
			{
				key: crypto.randomUUID(),
				id: null,
				questionCategoryId: null,
				question: '',
				questionDescription: '',
				sourceOfFunding: '',
				tasksList: [
					{
						key: crypto.randomUUID(),
						id: null,
						description: '',
						assignmentId: null,
						eventsList: [
							{
								key: crypto.randomUUID(),
								id: null,
								eventDetails: '',
								indicator: '',
								deadlineDate: null,
								kurultaiStatusId: null,
								responsibleAuthority: '',
							},
						],
					},
				],
			},
		]

		const assignments = props.kurultaiRegister.assignmentsList?.length
			? props.kurultaiRegister.assignmentsList.map(a => ({
					...a,
					tasksList: a.tasksList || [],
			  }))
			: defaultAssignments

		setItems(assignments)
		updateGeoJsonPoint()
	}

	const setEntityFromFields = (): EntKurultaiRegister => {
		const updatedRegister = _.cloneDeep(props.kurultaiRegister)
		updatedRegister.assemblyNumber = form.getFieldValue(FormField.assemblyNumber)
		const dateValue = form.getFieldValue(FormField.assemblyDate)
		updatedRegister.assemblyDate = dateValue ? dayjs(dateValue).format('YYYY-MM-DD') : null
		updatedRegister.delegateName = form.getFieldValue(FormField.delegateName)
		updatedRegister.regionId = form.getFieldValue(FormField.territorialUnitRegion)
		updatedRegister.areaId = selectedAreaId
		updatedRegister.territorialUnitId = form.getFieldValue(FormField.territorialUnit)

		updatedRegister.assignmentsList = items.map(assignment => ({
			...assignment,
			tasksList: (assignment.tasksList || []).map(task => ({
				...task,
				eventsList: task.eventsList || [],
			})),
		}))

		if (geoJsonData && geoJsonData.geometry && geoJsonData.geometry.coordinates) {
			const coords = geoJsonData.geometry.coordinates as number[]
			updatedRegister.longitude = coords[0]
			updatedRegister.latitude = coords[1]
		}

		return updatedRegister
	}

	const hideModal = () => {
		props.onHide()
		setItems([])
		setSelectedRegionPolygonJson(null)
		setSelectedAreaCode(null)
		setSelectedAreaId(null)
		setSelectedRegionId(null)
		setGeoJsonData(null)
	}

	const handleSubmit = async () => {
		setLoading(true)
		try {
			await form.validateFields()

			if (!geoJsonData) {
				setGeoError(true)
				throw new Error('Не выбрана точка на карте')
			}

			if (!items || items.length === 0) {
				throw new Error('Список поручений не должен быть пустым!')
			}

			const data = setEntityFromFields()
			const result = await props.onSubmit(data)
			if (result) hideModal()
		} catch (error: any) {
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	return (
		<DraggableModal
			title={props.kurultaiRegister.id === null ? t('kurultai.add_note') : t('kurultai.edit_note')}
			visible={props.show}
			onCancel={hideModal}
			onSubmit={handleSubmit}
			submitLoading={loading}
			width={800}
		>
			<Form autoComplete='off' layout='vertical' form={form}>
				<Row gutter={16}>
					<Col span={12}>
						<Form.Item
							label={t('kurultai.assembly_number')}
							name={FormField.assemblyNumber}
							rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
						>
							<Input />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label={t('kurultai.delegate_name')}
							name={FormField.delegateName}
							rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
						>
							<Input type='string' />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label={t('kurultai.assemblyDate')}
							name={FormField.assemblyDate}
							rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
						>
							<DatePicker format={dateFormat} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label={t('titles.territorialUnitRegion')}
							name={FormField.territorialUnitRegion}
							rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
						>
							<SelectTerritorialUnitsCode
								parentId={1}
								onChange={(value, option: any) => {
									setSelectedRegionId(Number(value))
									setSelectedRegionPolygonJson(option?.data?.polygonJson)
									setSelectedRegionCode(option?.data?.code)
									setSelectedAreaCode(null)
									setSelectedAreaId(null)
									form.setFieldsValue({
										[FormField.territorialUnitRegion]: value,
										[FormField.territorialUnitArea]: null,
										[FormField.territorialUnit]: null,
									})
								}}
							/>
						</Form.Item>
						<>
							{selectedRegionId && (
								<Form.Item
									label={t('titles.territorialUnitDistricts')}
									name={FormField.territorialUnitArea}
									rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
								>
									<SelectTerritorialUnitsCode
										type={[
											'AREA',
											'CITY_OF_REPUBLICAN_SIGNIFICANCE',
											'CITY_OF_REGIONAL_SIGNIFICANCE',
										]}
										parentId={selectedRegionId}
										onChange={(value, option: any) => {
											setSelectedRegionPolygonJson(option?.data?.polygonJson)
											setSelectedAreaCode(option?.data?.code || null)
											setSelectedAreaId(value as number)
											form.setFields([{ name: FormField.territorialUnit, value: null }])
										}}
									/>
								</Form.Item>
							)}
							{selectedAreaCode && (
								<Form.Item
									label={t('titles.territorialUnitVillage')}
									name={FormField.territorialUnit}
									rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
								>
									<SelectTerritorialUnitsCode areaCode={selectedAreaCode} />
								</Form.Item>
							)}
						</>
					</Col>
				</Row>

				{/* Блок поручений */}
				<AddKurultaiItems items={items ?? []} setItems={setItems} />

				{/* Карта с возможностью поставить одну точку */}
				<div style={{ marginTop: '15px' }}>
					<MapAddSingleMarker
						polygonJson={selectedRegionPolygonJson}
						setGeoJsonData={data => {
							setGeoJsonData(data)
							if (data) setGeoError(false)
						}}
						geoJsonData={geoJsonData}
					/>
				</div>

				{geoError && (
					<div
						style={{
							color: '#ff4d4f',
							marginTop: 8,
							fontWeight: '600',
							fontSize: '16px',
							textAlign: 'center',
						}}
					>
						{'Необходимо указать точку на карте'}
					</div>
				)}
			</Form>
		</DraggableModal>
	)
}
